// src/Pages/CategoryProducts/CategoryProducts.js

import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./ArchivalProducts.css";

const CategoryProducts = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({
    subCategory: "",
    weight: "",
    size: "",
    carat: "",
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // New states for button and loading
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isProductInCart, setIsProductInCart] = useState(false);

  // 🔍 Zoom & Pan states
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // 📱 Swipe states
  const touchStartRef = useRef(null);

  useEffect(() => {
    if (selectedProduct || showFilterPopup) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [selectedProduct, showFilterPopup]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const q = query(
          collection(db, "products"),
          where("category", "==", categoryName)
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setProducts(data);
      } catch (err) {
        console.error("❌ Error fetching products:", err);
      }
    };
    fetchProducts();
  }, [categoryName]);

  // Refactored: Check if the product is in the cart
  const checkCartStatus = async (product) => {
    if (product) {
      try {
        const q = query(
          collection(db, "carts"),
          where("productId", "==", product.id)
        );
        const snap = await getDocs(q);
        setIsProductInCart(!snap.empty);
      } catch (error) {
        console.error("Error checking cart status:", error);
        setIsProductInCart(false);
      }
    }
  };

  // Check if the product is in the cart when the modal opens
  useEffect(() => {
    checkCartStatus(selectedProduct);
  }, [selectedProduct]);

  const filteredProducts = products.filter((p) => {
    if (filters.subCategory && p.subCategory !== filters.subCategory)
      return false;

    if (filters.weight) {
      const enteredWeight = parseFloat(filters.weight);
      const minWeight = enteredWeight - 10;
      const maxWeight = enteredWeight + 20;
      if (parseFloat(p.weight) < minWeight || parseFloat(p.weight) > maxWeight)
        return false;
    }

    if (filters.size && p.size !== filters.size) return false;
    if (filters.carat && p.carat !== filters.carat) return false;
    return true;
  });

  // 🔍 Handle scroll zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    setZoom((prev) => {
      let newZoom = prev + delta;
      if (newZoom < 1) newZoom = 1;
      if (newZoom > 2) newZoom = 2;
      return newZoom;
    });
  };

  // 🖱 Handle Drag with mouse
  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  // 📱 Handle Touch Events
  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
    if (zoom <= 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      dragStart.current = {
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      };
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    if (zoom > 1) {
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.current.x,
        y: touch.clientY - dragStart.current.y,
      });
    }
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
    if (zoom > 1) return; // Disable swipe if zoomed in

    if (touchStartRef.current !== null) {
      const touchEnd = e.changedTouches[0].clientX;
      const swipeDistance = touchEnd - touchStartRef.current;
      const swipeThreshold = 50;

      if (swipeDistance > swipeThreshold) {
        goToPreviousProduct();
      } else if (swipeDistance < -swipeThreshold) {
        goToNextProduct();
      }
      touchStartRef.current = null;
    }
  };

  // ♻️ Reset zoom & position
  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // Product Navigation Handlers
  const goToNextProduct = () => {
    const currentIndex = filteredProducts.findIndex(
      (p) => p.id === selectedProduct.id
    );
    const nextIndex = (currentIndex + 1) % filteredProducts.length;
    setSelectedProduct(filteredProducts[nextIndex]);
    resetZoom();
  };

  const goToPreviousProduct = () => {
    const currentIndex = filteredProducts.findIndex(
      (p) => p.id === selectedProduct.id
    );
    const previousIndex =
      (currentIndex - 1 + filteredProducts.length) % filteredProducts.length;
    setSelectedProduct(filteredProducts[previousIndex]);
    resetZoom();
  };

  // Image Slider Handlers
  const handleNextImage = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex + 1) % selectedProduct.images.length
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(
      (prevIndex) =>
        (prevIndex - 1 + selectedProduct.images.length) %
        selectedProduct.images.length
    );
  };

  // Handle clearing all filters
  const handleClearFilters = () => {
    setFilters({
      subCategory: "",
      weight: "",
      size: "",
      carat: "",
    });
  };

  // Add to Cart Handler
  const handleAddToCart = async () => {
    if (!selectedProduct) return;
    setIsAddingToCart(true);

    try {
      await addDoc(collection(db, "carts"), {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        image: selectedProduct.images?.[0],
        weight: selectedProduct.weight,
        size: selectedProduct.size,
        carat: selectedProduct.carat,
        createdAt: serverTimestamp(),
      });

      setIsAddingToCart(false);
      setIsProductInCart(true);
    } catch (error) {
      console.error("Error adding to cart:", error);
      setIsAddingToCart(false);
      alert("Failed to add product to cart. Please try again.");
    }
  };

  // 🆕 Place Order Handler (FIXED)
  const handlePlaceOrder = () => {
    if (!selectedProduct) return;

    // Prepare the selected product data to be sent to the order page
    const orderItems = [
      {
        id: selectedProduct.id,
        productName: selectedProduct.name,
        image: selectedProduct.images?.[0],
        weight: selectedProduct.weight,
        size: selectedProduct.size,
        carat: selectedProduct.carat,
        // Include any other necessary fields
      },
    ];

    // Navigate to the checkout page, passing the single product
    navigate("/Checkout", { state: { itemsToOrder: orderItems } });

    // ⭐ FIX: Assume that the 'Place Order' process will remove the item from the cart.
    // Optimistically set the state so the button switches back to 'Add to Cart'.
    setIsProductInCart(false);
  };

  return (
    <div className="category-products">
      {/* Banner */}
      <section className="category-banner">
        <div className="overlay">
          <h1>{categoryName} Collection</h1>
          <p>Discover timeless elegance with {categoryName} designs.</p>
        </div>
      </section>

      <div className="content-wrapper">
        <aside className="filters">
          <h3>Filters</h3>
          <div className="filter-group">
            <label>Sub Category</label>
            <select
              value={filters.subCategory}
              onChange={(e) =>
                setFilters({ ...filters, subCategory: e.target.value })
              }
            >
              <option value="">All</option>
              <option value="Traditional">Traditional</option>
              <option value="Modern">Modern</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Weight</label>
            <input
              type="number"
              placeholder="e.g. 50"
              value={filters.weight}
              onChange={(e) =>
                setFilters({ ...filters, weight: e.target.value })
              }
            />
          </div>
          <div className="filter-group">
            <label>Size</label>
            <input
              type="text"
              placeholder="e.g. 18 inch"
              value={filters.size}
              onChange={(e) => setFilters({ ...filters, size: e.target.value })}
            />
          </div>
          <div className="filter-group">
            <label>Carat</label>
            <select
              value={filters.carat}
              onChange={(e) =>
                setFilters({ ...filters, carat: e.target.value })
              }
            >
              <option value="">All</option>
              <option value="22K">22K</option>
              <option value="18K">18K</option>
              <option value="Silver">Silver</option>
            </select>
          </div>
        </aside>

        <main className="products-grid">
          {filteredProducts.length > 0 ? (
            <div className="masonry-grid">
              {filteredProducts.map((p) => (
                <div
                  className="masonry-item"
                  key={p.id}
                  onClick={() => {
                    setSelectedProduct(p);
                    setCurrentImageIndex(0);
                    resetZoom();
                  }}
                >
                  <img src={p.images?.[0]} alt={p.name} />
                  <div className="overlay">
                    <span className="length">
                      {p.size || p.length || "-"} Inch
                    </span>
                    <span className="weight">{p.weight} gm</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-products">No products found.</p>
          )}
        </main>
      </div>

      <button
        className="filter-toggle-btn"
        onClick={() => setShowFilterPopup(true)}
      >
        Filters
      </button>

      {showFilterPopup && (
        <div className="filter-popup-modal">
          <div className="popup-content">
            <button
              className="close-btn"
              onClick={() => setShowFilterPopup(false)}
            >
              ✕
            </button>
            <h3>Filters</h3>
            <div className="filter-group">
              <label>Sub Category</label>
              <select
                value={filters.subCategory}
                onChange={(e) =>
                  setFilters({ ...filters, subCategory: e.target.value })
                }
              >
                <option value="">All</option>
                <option value="Traditional">Traditional</option>
                <option value="Modern">Modern</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Weight</label>
              <input
                type="number"
                placeholder="e.g. 50"
                value={filters.weight}
                onChange={(e) =>
                  setFilters({ ...filters, weight: e.target.value })
                }
              />
            </div>
            <div className="filter-group">
              <label>Size</label>
              <input
                type="text"
                placeholder="e.g. 18 inch"
                value={filters.size}
                onChange={(e) =>
                  setFilters({ ...filters, size: e.target.value })
                }
              />
            </div>
            <div className="filter-group">
              <label>Carat</label>
              <select
                value={filters.carat}
                onChange={(e) =>
                  setFilters({ ...filters, carat: e.target.value })
                }
              >
                <option value="">All</option>
                <option value="22K">22K</option>
                <option value="18K">18K</option>
                <option value="Silver">Silver</option>
              </select>
            </div>
            <div className="button-group">
              <button className="clear-btn" onClick={handleClearFilters}>
                Clear All
              </button>
              <button
                className="apply-btn"
                onClick={() => setShowFilterPopup(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedProduct && (
        <div className="product-modal">
          <div className="product-modal-content">
            <button
              className="close-btn"
              onClick={() => setSelectedProduct(null)}
            >
              ✕
            </button>
            {/* Left - Zoomable Image with Navigation */}
            <button className="nav-btn prev-btn" onClick={goToPreviousProduct}>
              &lt;
            </button>
            <div className="modal-left-container">
              <div
                className="modal-left"
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                  overflow: "hidden",
                  cursor:
                    zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                }}
              >
                {/* Main image with zoom/pan */}
                <img
                  src={selectedProduct.images?.[currentImageIndex]}
                  alt={selectedProduct.name}
                  className="zoomable-img"
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                    transformOrigin: "center center",
                    transition: isDragging ? "none" : "transform 0.1s ease-out",
                  }}
                />

                {/* Image slider controls */}
                {selectedProduct.images?.length > 1 && (
                  <div className="image-slider-controls">
                    <button
                      onClick={handlePrevImage}
                      className="image-nav-btn prev"
                    >
                      &lt;
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="image-nav-btn next"
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {selectedProduct.images?.length > 1 && (
                <div className="thumbnails">
                  {selectedProduct.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className={`thumbnail ${
                        index === currentImageIndex ? "active" : ""
                      }`}
                      onClick={() => {
                        setCurrentImageIndex(index);
                        resetZoom();
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="modal-right">
              <h2>{selectedProduct.name}</h2>
              <p>
                <strong>Weight:</strong> {selectedProduct.weight} gm
              </p>
              <p>
                <strong>Size:</strong> {selectedProduct.size || "-"} Inch
              </p>
              <p>
                <strong>Carat:</strong> {selectedProduct.carat}
              </p>
              <p>
                <strong>Sub Category:</strong> {selectedProduct.subCategory}
              </p>
              {selectedProduct.description && (
                <p className="description">{selectedProduct.description}</p>
              )}

              {/* 🆕 Place Order Button */}
              <button className="order-btn" onClick={handlePlaceOrder}>
                Place Order
              </button>

              {/* Conditional button rendering (Add to Cart / Go to Cart) */}
              {isProductInCart ? (
                <button className="order-btn" onClick={() => navigate("/cart")}>
                  Go to Cart
                </button>
              ) : (
                <button
                  className={`order-btn ${isAddingToCart ? "loading" : ""}`}
                  onClick={handleAddToCart}
                  disabled={isAddingToCart}
                >
                  {isAddingToCart ? (
                    <div className="spinner"></div>
                  ) : (
                    "Add to Cart"
                  )}
                </button>
              )}
            </div>

            <button className="nav-btn next-btn" onClick={goToNextProduct}>
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryProducts;
